/**
 * ServiceToggle — chip-with-affordance for the Add-to-Cart modal's Optional
 * Services / Features / Configuration section (Wave 2d, Bill 2026-05-06).
 *
 * Renders one feature row driven by `Feature.configSchema`:
 *
 *   • boolean only           → chip toggle
 *   • boolean + currency     → chip + inline $-input
 *   • boolean + integer      → chip + inline #-input
 *   • boolean + picker       → chip + inline dropdown
 *   • boolean + compound     → chip + ⚙ gear button → opens nested
 *                              OrderChecksConfigModal (or generic
 *                              CompoundConfigModal in the future)
 *   • boolean + sub-form     → child rendered ONLY when parent toggled
 *                              (regEOptIn requires odp on)
 *
 * Bill 2026-05-06: nothing else surfaces here. Compliance / Documents /
 * Follow-up are derived state — they live elsewhere in the flow.
 */
import { useEffect, useState, type ReactNode } from 'react';
import {
  parseFeatureConfigSchema,
  type Feature,
  type FeatureConfigSchema,
  type FeatureConfigCompound,
  type FeatureConfigPicker,
} from '@retail-ops/shared-types';
import { serviceIconUrl } from '@/icons/flat-library';
import { OrderChecksConfigModal, type OrderChecksValue } from './OrderChecksConfigModal';
import { applyOdpRegERule, fieldGateOpen, regEHidden, REG_E_KEY } from './odpRegE';

// ── Feature category whitelist (Sprint 2.5, Bill 2026-05-08) ───────────
// Defense-in-depth guard: even if seed drift reintroduces a Compliance /
// Document / FollowUp / CrossSell / EntityConfig row into Features, the chip
// will not render. The Features table is canonically curated to 6 codes
// across two categories (Account + Service) per
// project_features_catalog_canonical_20260508.md, but `Card`, `Banking`, and
// `PaymentService` are also accepted to leave room for future wire-level
// features that legitimately spawn auxiliary CIFs / records.
//
// Compliance gates → owned by rules engine (NOT shown as chips).
// Disclosures      → owned by disclosure engine.
// FollowUp / Tasks → Tasks subsystem.
// CrossSell        → CrossSell module.
// EntityConfig     → entity wizard (NOT a runtime decision).
const FEATURE_CATEGORY_WHITELIST = new Set<string>([
  'Account',
  'Service',
  'Card',
  'Banking',
  'PaymentService',
]);

// ── Picker source resolver ────────────────────────────────────────────
// Maps `configSchema.sourceRef` to an in-cache list of options. Today the
// resolver supports the canonical references; new sources can extend the
// switch without touching call sites.

export interface PickerOption {
  code: string;
  displayName: string;
}

export type PickerSourceMap = Partial<Record<string, ReadonlyArray<PickerOption>>>;

// ── Props ──────────────────────────────────────────────────────────────

export interface ServiceToggleProps {
  feature: Feature;
  /** Current cart-binding value for this featureCode. */
  value: unknown;
  onChange: (next: unknown) => void;
  /** Set of feature codes currently toggled on (used by sub-form gates). */
  toggledOn: ReadonlySet<string>;
  /** Picker option lookup. */
  pickerSources?: PickerSourceMap;
  /** Disabled (e.g. `Unavailable` state — no FSR decision). */
  disabled?: boolean;
  /** Required + locked-on (Bill 2026-07-17): renders checked with a "Required"
   *  badge and cannot be unchecked, but stays full-opacity (not greyed). */
  requiredLock?: boolean;
  /** When set, the toggle is unavailable and this reason renders inline
   *  (D4 overdraft employment-gate, Bill 2026-07-17). Pair with disabled. */
  unavailableReason?: string;
  /** FSR-facing note shown under the service while it is on — e.g. the
   *  check-order price and the free-checks rule (SystemSettings
   *  checks.pricingNote, Collette 2026-10-02, BACKLOG-1668). */
  note?: string;
}

export function ServiceToggle({
  feature,
  value,
  onChange,
  note,
  toggledOn,
  pickerSources,
  disabled = false,
  requiredLock = false,
  unavailableReason,
}: ServiceToggleProps): JSX.Element | null {
  // Defense-in-depth — drop chips for any category the curated catalog
  // forbids (Compliance / Disclosure / FollowUp / CrossSell / EntityConfig).
  // See FEATURE_CATEGORY_WHITELIST above.
  if (!FEATURE_CATEGORY_WHITELIST.has(feature.categoryCode)) {
    return null;
  }

  const schema = parseFeatureConfigSchema(feature.configSchema ?? null);

  // Dependency gating: hide the chip entirely when its parent service is off.
  // 2026-07-24 — generalized from boolean-only (which was ALSO dead code: the
  // schema parser rejected boolean schemas until today) to ANY schema type,
  // so admin-authored requiresParent works everywhere. First consumer:
  // electronicStatements requiresParent=internetBanking (e-statements ride
  // the NetTeller enrollment; the commit pipeline enforces the same rule).
  if (schema?.requiresParent && !toggledOn.has(schema.requiresParent)) {
    return null;
  }

  // Determine "is on" — for compound/currency/picker the chip is on when
  // value is non-null/truthy. For pure booleans, value === true.
  const isOn = schema?.type === 'compound' || schema?.type === 'picker' || schema?.type === 'currency' || schema?.type === 'integer'
    ? value != null && value !== false
    : Boolean(value);

  const toggle = (): void => {
    if (disabled || requiredLock) return;
    if (isOn) {
      onChange(undefined);
    } else {
      // Default-on value depends on shape.
      switch (schema?.type) {
        case 'currency':
        case 'integer':
          onChange(schema.defaultValue ?? 0);
          break;
        case 'picker':
          onChange(null); // FSR must pick — not auto-defaulted
          break;
        case 'compound': {
          const seed: Record<string, unknown> = {};
          for (const f of schema.fields) {
            if (f.type === 'currency' || f.type === 'integer') seed[f.key] = f.defaultValue ?? 0;
            // Reg E defaults to Opted-in when OD is turned on (Bill 2026-07-13
            // r2 / BACKLOG-0872); other pickers honor a string defaultValue.
            else if (f.type === 'picker' && typeof f.defaultValue === 'string') seed[f.key] = f.defaultValue;
            else if (f.type === 'picker' && f.key === 'regEOptIn') seed[f.key] = 'optIn';
            else seed[f.key] = null;
          }
          // Reg E only while overdraft protection is enrolled (2026-10-16).
          onChange(applyOdpRegERule(schema, seed));
          break;
        }
        default:
          onChange(true);
      }
    }
  };

  const chipCls = ['rao-svc__chip', isOn ? 'is-on' : '', disabled ? 'is-disabled' : '']
    .filter(Boolean).join(' ');

  // Bill 2026-05-13 — service icon from flat-library when feature.code matches
  // a canonical-5 service slug (debit-card / order-checks / e-statements /
  // internet-banking / overdraft-protection). Resolver handles alias variants.
  const iconUrl = serviceIconUrl(feature.code);
  // Bill 2026-07-13 r2 / BACKLOG-0874 — services render as icon TILES (icon +
  // label + on-state) with the modifier inline INSIDE the tile, not a plain
  // check-labeled button. `chipCls` retained for back-compat consumers.
  void chipCls;
  const tileCls = ['rao-svc__tile', isOn ? 'is-on' : '', disabled ? 'is-disabled' : '', requiredLock ? 'is-required' : '']
    .filter(Boolean).join(' ');

  return (
    <div
      className={tileCls}
      role="button"
      tabIndex={disabled || requiredLock ? -1 : 0}
      aria-pressed={isOn}
      aria-disabled={requiredLock || undefined}
      aria-label={`${feature.displayName} ${isOn ? 'enabled' : 'disabled'}${requiredLock ? ' — required, cannot be removed' : ''}`}
      onClick={(e) => {
        // Clicks on the inline modifier must not toggle the tile off.
        if ((e.target as HTMLElement).closest('.rao-svc__tile-mod')) return;
        toggle();
      }}
      onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && (e.target === e.currentTarget)) { e.preventDefault(); toggle(); } }}
    >
      <div className="rao-svc__tile-head">
        {iconUrl && (
          <img src={iconUrl} alt="" aria-hidden width={22} height={22} className="rao-svc__tile-icon" />
        )}
        <span className="rao-svc__tile-label">{feature.displayName}</span>
        {isOn && <span aria-hidden="true" className="rao-svc__tile-check">✓</span>}
        {requiredLock && (
          <span
            className="rao-svc__tile-req"
            title="Required for this product — cannot be removed"
            style={{
              marginLeft: 6,
              fontSize: '0.6875rem',
              fontWeight: 700,
              letterSpacing: '0.02em',
              color: 'var(--pcb-blue-dark)',
              background: 'var(--info-bg)',
              borderRadius: 4,
              padding: '1px 6px',
              textTransform: 'uppercase',
            }}
          >
            Required
          </span>
        )}
      </div>

      {unavailableReason && (
        <div
          className="rao-svc__tile-unavail"
          style={{ fontSize: '0.6875rem', color: 'var(--pcb-red)', marginTop: 4, lineHeight: 1.3 }}
        >
          {unavailableReason}
        </div>
      )}

      {/* Inline modifier — inside the tile, only when toggled on. */}
      {isOn && schema && schema.type !== 'boolean' && (
        <div className="rao-svc__tile-mod">
          <ServiceToggleAffordance
            schema={schema}
            value={value}
            onChange={onChange}
            pickerSources={pickerSources}
          />
        </div>
      )}
      {isOn && note && (
        <div
          className="rao-svc__tile-note"
          style={{ fontSize: '0.6875rem', color: 'var(--ink-600)', marginTop: 6, lineHeight: 1.35 }}
        >
          {note}
        </div>
      )}
    </div>
  );
}

// ── Affordance dispatch ────────────────────────────────────────────────

interface AffordanceProps {
  schema: FeatureConfigSchema;
  value: unknown;
  onChange: (next: unknown) => void;
  pickerSources?: PickerSourceMap;
}

function ServiceToggleAffordance({ schema, value, onChange, pickerSources }: AffordanceProps): ReactNode {
  switch (schema.type) {
    case 'boolean':
      return null; // No secondary input
    case 'currency':
      return (
        <CurrencyAffordance
          value={typeof value === 'number' ? value : (schema.defaultValue ?? 0)}
          min={schema.min}
          max={schema.max}
          label={schema.label}
          onChange={(n) => onChange(n)}
        />
      );
    case 'integer':
      return (
        <IntegerAffordance
          value={typeof value === 'number' ? value : (schema.defaultValue ?? 0)}
          min={schema.min}
          max={schema.max}
          label={schema.label}
          onChange={(n) => onChange(n)}
        />
      );
    case 'picker':
      return (
        <PickerAffordance
          schema={schema}
          value={typeof value === 'string' ? value : ''}
          onChange={(v) => onChange(v)}
          pickerSources={pickerSources}
        />
      );
    case 'compound':
      return (
        <CompoundAffordance
          schema={schema}
          value={(value && typeof value === 'object') ? (value as Record<string, unknown>) : {}}
          onChange={(v) => onChange(v)}
          pickerSources={pickerSources}
        />
      );
    default:
      return null;
  }
}

// ── Currency input ─────────────────────────────────────────────────────

function CurrencyAffordance({
  value, min, max, label, onChange,
}: { value: number; min?: number; max?: number; label?: string; onChange: (n: number) => void }): JSX.Element {
  return (
    <label className="rao-svc__amt">
      <span aria-hidden="true" className="rao-svc__amt-prefix">$</span>
      <input
        type="number"
        className="rao-svc__amt-input"
        value={value}
        min={min}
        max={max}
        step={1}
        aria-label={label ?? 'Amount'}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

// ── Integer input ──────────────────────────────────────────────────────

function IntegerAffordance({
  value, min, max, label, onChange,
}: { value: number; min?: number; max?: number; label?: string; onChange: (n: number) => void }): JSX.Element {
  return (
    <label className="rao-svc__int">
      <input
        type="number"
        className="rao-svc__int-input"
        value={value}
        min={min}
        max={max}
        step={1}
        aria-label={label ?? 'Number'}
        onChange={(e) => onChange(Number(e.target.value))}
      />
    </label>
  );
}

// ── Picker dropdown ────────────────────────────────────────────────────

function PickerAffordance({
  schema, value, onChange, pickerSources,
}: {
  schema: FeatureConfigPicker;
  value: string;
  onChange: (v: string) => void;
  pickerSources?: PickerSourceMap;
}): JSX.Element {
  const options = (pickerSources && pickerSources[schema.sourceRef]) ?? [];
  return (
    <select
      className="rao-svc__picker"
      value={value}
      aria-label={schema.label ?? 'Pick an option'}
      onChange={(e) => onChange(e.target.value)}
    >
      <option value="">— select —</option>
      {options.map((o) => (
        <option key={o.code} value={o.code}>{o.displayName}</option>
      ))}
    </select>
  );
}

// ── Compound (inline fields) ───────────────────────────────────────────
// Bill 2026-07-13 Item 12: the old "⚙ Configure…" gear hid Reg E opt-in/out
// behind an undiscoverable nested modal (which also couldn't hydrate
// sourceRef pickers). Compound fields now render INLINE under the chip so
// every decision the schema asks for is visible the moment the feature is
// toggled on. OrderChecksConfigModal is retained only as the legacy nested
// editor for callers that still import it directly.

function CompoundAffordance({
  schema, value, onChange, pickerSources,
}: {
  schema: FeatureConfigCompound;
  value: Record<string, unknown>;
  onChange: (v: Record<string, unknown>) => void;
  pickerSources?: PickerSourceMap;
}): JSX.Element {
  // Overdraft declined / revoked -> Reg E opted out and hidden (MacKenzie Mott 2026-10-16;
  // Bill approved). Applied on every edit, and once on load for a line saved before the rule.
  const update = (key: string, v: unknown): void => {
    onChange(applyOdpRegERule(schema, { ...value, [key]: v }));
  };
  useEffect(() => {
    const fixed = applyOdpRegERule(schema, value);
    if (fixed !== value) onChange(fixed);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schema, value]);

  // REQ-184 (Bill 2026-07-25, ODP Variant C) — generic sibling-gate: a field
  // whose schema carries `enabledWhen:{key,equals}` disables (greyed, inert)
  // unless the named sibling holds that value. A MISSING (or null) sibling counts
  // as its defaultValue so the gate is correct before first touch.
  const fieldEnabled = (f: FeatureConfigPicker): boolean => fieldGateOpen(schema, value, f);

  return (
    <div className="rao-svc__compound" style={{ display: 'flex', flexWrap: 'wrap', gap: '8px 16px', alignItems: 'flex-end' }}>
      {schema.fields.map((f) => {
        // A gated PICKER (e.g. Reg E, enabledWhen odpStatus=enrolled) is hidden rather than
        // greyed when its gate is closed — there is no choice to make (2026-10-16).
        if (f.type === 'picker' && (!fieldEnabled(f) || (f.key === REG_E_KEY && regEHidden(schema, value)))) {
          return null;
        }
        // Inline `options` win; otherwise hydrate from pickerSources[sourceRef].
        const pickerOptions: ReadonlyArray<{ code: string; label: string }> =
          Array.isArray(f.options) && f.options.length > 0
            ? f.options
            : ((pickerSources && typeof f.sourceRef === 'string' && pickerSources[f.sourceRef]) || [])
                .map((o) => ({ code: o.code, label: o.displayName }));
        return (
          <label
            key={f.key}
            style={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            // Segment fields hold BUTTONS; a bare caption click would otherwise
            // synthetically activate the FIRST button (silently forcing
            // "enrolled"). Kill the label's implicit activation for those.
            onClick={f.type === 'segment' ? (e) => {
              if (!(e.target as HTMLElement).closest('button')) e.preventDefault();
            } : undefined}
          >
            <span style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-600, #64748b)' }}>
              {f.label ?? f.key}
            </span>
            {(f.type === 'currency' || f.type === 'integer') && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
                {f.type === 'currency' && <span aria-hidden="true">$</span>}
                <input
                  type="number"
                  className="rao-svc__amt-input"
                  min={f.min}
                  max={f.max}
                  step={1}
                  value={typeof value[f.key] === 'number' ? Number(value[f.key]) : (f.defaultValue ?? 0)}
                  aria-label={f.label ?? f.key}
                  onChange={(e) => update(f.key, Number(e.target.value))}
                />
              </span>
            )}
            {f.type === 'picker' && (
              <select
                className="rao-svc__picker"
                value={typeof value[f.key] === 'string' ? String(value[f.key]) : ''}
                aria-label={f.label ?? f.key}
                onChange={(e) => update(f.key, e.target.value)}
              >
                <option value="">— select —</option>
                {pickerOptions.map((o) => (
                  <option key={o.code} value={o.code}>{o.label}</option>
                ))}
              </select>
            )}
            {f.type === 'boolean' && (
              <input
                type="checkbox"
                checked={Boolean(value[f.key])}
                aria-label={f.label ?? f.key}
                onChange={(e) => update(f.key, e.target.checked)}
              />
            )}
            {f.type === 'text' && (
              <input
                type="text"
                className="rao-svc__amt-input"
                value={typeof value[f.key] === 'string' ? String(value[f.key]) : ''}
                disabled={!fieldEnabled(f)}
                aria-label={f.label ?? f.key}
                onChange={(e) => update(f.key, e.target.value)}
              />
            )}
            {/* REQ-184 Variant C (walkthrough 1:00:01) — snap-only slider over
                admin-configured stops; "you don't even have a dropdown for it".
                Stored value is the DOLLAR AMOUNT (back-compat with the currency
                shape seeds 114-era carts persisted). */}
            {f.type === 'slider' && (() => {
              const stops: number[] = Array.isArray(f.stops)
                ? (f.stops as unknown[]).filter((n): n is number => typeof n === 'number')
                : [];
              if (stops.length === 0) return null;
              const enabled = fieldEnabled(f);
              const current = typeof value[f.key] === 'number' ? Number(value[f.key]) : (f.defaultValue ?? stops[stops.length - 1]);
              // Snap any persisted amount (e.g. legacy free-entry 750) to the nearest stop.
              const idx = stops.reduce((best, s, i) => (Math.abs(s - current) < Math.abs(stops[best] - current) ? i : best), 0);
              return (
                <span style={{ display: 'flex', flexDirection: 'column', gap: 2, minWidth: 220 }}>
                  <input
                    type="range"
                    min={0}
                    max={stops.length - 1}
                    step={1}
                    value={idx}
                    disabled={!enabled}
                    aria-label={f.label ?? f.key}
                    aria-valuetext={enabled ? `$${stops[idx]}` : 'not applicable'}
                    style={{ width: '100%', accentColor: enabled ? 'var(--pcb-blue)' : 'var(--ink-500)', opacity: enabled ? 1 : 0.45 }}
                    onChange={(e) => update(f.key, stops[Number(e.target.value)])}
                  />
                  <span aria-hidden="true" style={{ display: 'flex', justifyContent: 'space-between', fontSize: 10, color: 'var(--ink-600, #64748b)', opacity: enabled ? 1 : 0.45 }}>
                    {stops.map((s) => <span key={s}>${s}</span>)}
                  </span>
                  <span style={{ fontSize: 13, fontWeight: 700, color: enabled ? 'var(--pcb-blue-dark)' : 'var(--ink-500)', textAlign: 'center' }}>
                    {enabled ? `$${stops[idx]}` : '—'}
                  </span>
                </span>
              );
            })()}
            {/* REQ-184 — tri-state segment (Enrolled / Declined / Revoked).
                tone on the schema option colors the selected pill; the
                enrolled/declined/revoked SEMANTICS live in the schema + commit
                pipeline, not here. */}
            {f.type === 'segment' && (
              <span role="group" aria-label={f.label ?? f.key} style={{ display: 'flex', border: '1px solid var(--line-300, var(--surface-border))', borderRadius: 6, overflow: 'hidden' }}>
                {(Array.isArray(f.options) ? f.options : []).map((o) => {
                  const opt = o as { code: string; label: string; tone?: string; helpText?: string };
                  const selected = (typeof value[f.key] === 'string' ? value[f.key] : f.defaultValue) === opt.code;
                  const toneBg = opt.tone === 'danger' ? 'var(--err-fill)' : opt.tone === 'warn' ? 'var(--warn-bg)' : 'var(--ok-fill)';
                  const toneFg = opt.tone === 'warn' ? 'var(--ink-800)' : 'var(--on-brand)';
                  return (
                    <button
                      key={opt.code}
                      type="button"
                      title={opt.helpText}
                      aria-pressed={selected}
                      style={{
                        flex: 1,
                        border: 'none',
                        padding: '6px 10px',
                        font: 'inherit',
                        fontSize: 12,
                        cursor: 'pointer',
                        background: selected ? toneBg : 'transparent',
                        color: selected ? toneFg : 'var(--ink-600, #64748b)',
                        fontWeight: selected ? 600 : 400,
                      }}
                      onClick={() => update(f.key, opt.code)}
                    >
                      {opt.label}
                    </button>
                  );
                })}
              </span>
            )}
          </label>
        );
      })}
    </div>
  );
}
